import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { L } from "@/constants/labels";
import IncompleteProfileBanner from "@/components/IncompleteProfileBanner";
import GeoConfirmBanner from "@/components/GeoConfirmBanner";
import EmailVerificationBanner from "@/components/EmailVerificationBanner";
import AssistantWidget from "@/components/AssistantWidget";
import RoleSwitcher from "@/components/RoleSwitcher";
import {
  LayoutDashboard, Users, ClipboardList, MessageSquare, Briefcase,
  UserCircle, FileText, CalendarDays, Receipt, Gift, Shield, LogOut, LogIn, Menu, X, Building2
, ClipboardCheck } from "lucide-react";

export const LOGO = "https://customer-assets-wrfwihn1.emergentagent.net/job_chantier-link-1/artifacts/qk3h24jw_logo-primary-2026-07-19.webp";
// Teinte alignée sur --accent (#E8710A) — même orange que le bouton
// "Candidater à des chantiers" du bloc prestataire (btn-accent).
const LOGO_TINT = { filter: "brightness(0) saturate(100%) invert(45%) sepia(85%) saturate(1832%) hue-rotate(1deg) brightness(96%) contrast(94%)" };
export { LOGO_TINT };

function itemsFor(role) {
  const base = [];
  if (role === "entreprise") {
    base.push({ to: "/app/dashboard", label: L.nav.tableauDeBord, icon: LayoutDashboard });
    base.push({ to: "/app/prestataires", label: L.nav.prestataires, icon: Users });
    base.push({ to: "/app/chantiers", label: L.nav.besoins, icon: ClipboardList });
    base.push({ to: "/app/missions", label: L.nav.missions, icon: Briefcase });
    base.push({ to: "/app/commandes", label: "Commandes", icon: ClipboardCheck });
    base.push({ to: "/app/factures", label: L.nav.factures, icon: Receipt });
    base.push({ to: "/app/mon-organisation", label: "Mon organisation", icon: Building2 });
    base.push({ to: "/app/parrainage", label: "Parrainage", icon: Gift });
    base.push({ to: "/app/messages", label: L.nav.messages, icon: MessageSquare });
  } else if (role === "prestataire") {
    base.push({ to: "/app/dashboard", label: L.nav.tableauDeBord, icon: LayoutDashboard });
    base.push({ to: "/app/chantiers", label: L.nav.besoins, icon: ClipboardList });
    base.push({ to: "/app/mon-profil", label: L.nav.monProfil, icon: UserCircle });
    base.push({ to: "/app/mes-documents", label: L.nav.mesDocuments, icon: FileText });
    base.push({ to: "/app/mon-calendrier", label: L.nav.monCalendrier, icon: CalendarDays });
    base.push({ to: "/app/messages", label: L.nav.messages, icon: MessageSquare });
    base.push({ to: "/app/missions", label: L.nav.missions, icon: Briefcase });
    base.push({ to: "/app/commandes", label: "Commandes reçues", icon: ClipboardCheck });
    base.push({ to: "/app/factures", label: L.nav.factures, icon: Receipt });
  } else if (role === "admin") {
    base.push({ to: "/app/admin", label: L.nav.admin, icon: Shield });
    base.push({ to: "/app/admin/commandes-mentions", label: "Mentions commandes", icon: ClipboardCheck });
    base.push({ to: "/app/admin/commission", label: "Commission", icon: Receipt });
    base.push({ to: "/app/factures", label: L.nav.factures, icon: Receipt });
  }
  return base;
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = React.useState(false);
  const items = itemsFor(user?.role);

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-white flex flex-col md:flex-row">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex md:w-60 flex-col border-r border-[#E4E7EB] bg-[#F5F6F7]">
        <Link to="/" className="px-4 py-4 border-b border-[#E4E7EB] bg-white" data-testid="sidebar-logo">
          <img src={LOGO} style={LOGO_TINT} alt={L.brand.name} className="h-8 object-contain" />
        </Link>
        <nav className="flex-1 py-3">
          {items.map(it => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`nav-${it.to.split("/").pop()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm font-medium border-l-4 min-h-[44px] ${
                  isActive ? "bg-white border-[#1B3A5C] text-[#1B3A5C]" : "border-transparent text-[#1F2933] hover:bg-white"
                }`
              }
            >
              <it.icon size={18} />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#E4E7EB] text-xs text-[#3E4C59] bg-white">
          <div className="font-semibold text-[#1F2933]">{user?.raison_sociale}</div>
          <div className="truncate">{user?.email}</div>
          <button data-testid="logout-btn" onClick={doLogout} className="mt-2 btn-outline w-full">
            <LogOut size={16} /> {L.nav.deconnexion}
          </button>
        </div>
        <RoleSwitcher />
      </aside>

      {/* Mobile header */}
      <header className="md:hidden flex items-center justify-between px-3 py-2 border-b border-[#E4E7EB] bg-white">
        <Link to="/"><img src={LOGO} style={LOGO_TINT} alt="" className="h-7" /></Link>
        <button className="btn-ghost p-2" onClick={() => setOpen(true)} aria-label="Menu" data-testid="mobile-menu-btn">
          <Menu size={22} />
        </button>
      </header>

      {open && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white flex flex-col">
            <div className="flex items-center justify-between p-3 border-b border-[#E4E7EB]">
              <img src={LOGO} style={LOGO_TINT} alt="" className="h-7" />
              <button className="btn-ghost p-2" onClick={() => setOpen(false)}><X size={22} /></button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {items.map(it => (
                <NavLink key={it.to} to={it.to} onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 border-b border-[#E4E7EB] min-h-[48px] ${
                      isActive ? "bg-[#F5F6F7] text-[#1B3A5C] font-semibold" : "text-[#1F2933]"
                    }`}>
                  <it.icon size={18} /> {it.label}
                </NavLink>
              ))}
            </div>
            <button onClick={doLogout} className="btn-outline m-3" data-testid="mobile-logout-btn">
              <LogOut size={16} /> {L.nav.deconnexion}
            </button>
            <div className="border-t border-[#E4E7EB]"><RoleSwitcher /></div>
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 pb-20 md:pb-0">
        <div className="p-4 md:p-6 max-w-7xl space-y-4">
          <EmailVerificationBanner />
          <IncompleteProfileBanner />
          <GeoConfirmBanner />
          {children}
        </div>
      </main>

      {/* Bottom nav mobile */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-[#E4E7EB] flex justify-around z-30">
        {items.slice(0, 5).map(it => (
          <NavLink key={it.to} to={it.to}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center py-2 text-[11px] min-h-[56px] ${
                isActive ? "text-[#1B3A5C] font-semibold" : "text-[#3E4C59]"
              }`}
            data-testid={`bottom-nav-${it.to.split("/").pop()}`}>
            <it.icon size={20} />
            <span className="mt-0.5">{it.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Assistant contextuel — présent sur toutes les pages authentifiées, absent sur la Landing */}
      <AssistantWidget />
    </div>
  );
}

export function PublicHeader({ overlay = false }) {
  return (
    <header className={`sticky top-0 z-30 backdrop-blur-xl ${overlay ? "bg-[#0F2338]/70 border-b border-white/10" : "bg-white/80 border-b border-[#1B3A5C]/10"}`}>
      <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
        <Link to="/" aria-label={L.brand.name}>
          <img src={LOGO} style={overlay ? { filter: "brightness(0) invert(1)" } : LOGO_TINT} alt="" className="h-6 md:h-7 object-contain" />
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/connexion" className={`btn rounded-full ${overlay ? "border border-white/40 text-white hover:bg-white hover:text-[#0F2338]" : "border border-[#1B3A5C] text-[#1B3A5C] bg-white hover:bg-[#F5F6F7]"}`} data-testid="header-login-btn">
            <LogIn size={16} /> {L.nav.connexion}
          </Link>
          <Link to="/inscription" className="btn-accent rounded-full hidden md:inline-flex" data-testid="header-register-btn">
            {L.nav.inscription}
          </Link>
        </div>
      </div>
    </header>
  );
}

export function PublicFooter() {
  return (
    <footer className="border-t border-[#1B3A5C]/10 bg-[#F5F3EE] py-8 font-body-l">
      <div className="max-w-6xl mx-auto px-4 flex flex-col items-center gap-3 text-sm text-[#475569]">
        <img src={LOGO} style={LOGO_TINT} alt="" className="h-6 opacity-80" />
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
          <Link to="/mentions-legales" data-testid="footer-mentions">{L.legal.mentions}</Link>
          <Link to="/cgu" data-testid="footer-cgu">{L.legal.cgu}</Link>
          <Link to="/cgv" data-testid="footer-cgv">{L.legal.cgv}</Link>
          <Link to="/confidentialite" data-testid="footer-rgpd">{L.legal.confidentialite}</Link>
          <Link to="/responsabilite-rgpd" data-testid="footer-responsabilite">{L.legal.responsabilite}</Link>
          <Link to="/cookies" data-testid="footer-cookies">{L.legal.cookies}</Link>
          <a href="mailto:contact@voscompagnons.fr" className="text-[#1B3A5C] underline" data-testid="footer-contact">contact@voscompagnons.fr</a>
        </div>
        <span className="text-xs">© {new Date().getFullYear()} {L.brand.name}</span>
      </div>
    </footer>
  );
}
